import './Portrait.css';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { Candidate, CandidatePhoto } from '../contracts/static';
import { initials } from '../format/initials';
import { partyClass, partyOf } from '../theme/parties';

export type PortraitShape = 'square' | 'round';
/** Tratamento escolhido globalmente, nunca por candidato (D12, §16.4): `framed` (padrão) ou `cutout`. */
export type PortraitVariant = 'framed' | 'cutout';
export type PortraitState = 'empty' | 'loading' | 'loaded' | 'error';

export interface PortraitProps {
  /** Nome de exibição e partido (Candidate, FirstRoundPerson, ArchiveCandidate…). */
  candidate: Pick<Candidate, 'display_name' | 'party_code'>;
  /** Foto já resolvida pelo dono: `null` sem foto ou com `features.photos = false` (DE-13). */
  photo: CandidatePhoto | null;
  /** Lado da caixa em px (catálogo de tamanhos em §3.8; os dois candidatos da disputa usam o mesmo). */
  size: number;
  shape?: PortraitShape;
  variant?: PortraitVariant;
  /** Candidatura anulada (arquivo do 1º turno): opacidade .72 e foto em cinza. */
  annulled?: boolean;
  /** Derrotado em lista do arquivo: opacidade .45. */
  muted?: boolean;
  /** `eager` só no HeroLeaders e no PreResultsGrid (com fetchpriority="low", §15.7). */
  loading?: 'lazy' | 'eager';
}

/** Retrato com fundo na cor do partido (§3.8): foto quando houver; iniciais por baixo enquanto carrega, na falha e sem foto. */
export function Portrait({
  candidate,
  photo,
  size,
  shape = 'square',
  variant = 'framed',
  annulled = false,
  muted = false,
  loading = 'lazy',
}: PortraitProps) {
  const src = photo?.src ?? null;
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Foto já no cache do navegador pode terminar antes de o ouvinte existir.
  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img && src && img.complete && img.naturalWidth > 0) setLoadedSrc(src);
  }, [src]);

  const state: PortraitState = !src ? 'empty' : failedSrc === src ? 'error' : loadedSrc === src ? 'loaded' : 'loading';
  const cls = [
    'portrait',
    `portrait--${shape}`,
    `portrait--${variant}`,
    partyClass(partyOf(candidate)),
    annulled && 'portrait--annulled',
    muted && 'portrait--muted',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      class={cls}
      data-testid="portrait"
      data-state={state}
      data-dataviz=""
      aria-hidden="true"
      style={{ '--portrait-size': `${size}px`, '--portrait-fs': `${Math.round(size * 0.3)}px` }}
    >
      <span class="portrait__initials">{initials(candidate.display_name)}</span>
      {photo && state !== 'error' && (
        <img
          ref={imgRef}
          class="portrait__img"
          src={photo.src}
          srcset={photo.src_large ? `${photo.src} 96w, ${photo.src_large} 192w` : undefined}
          sizes={photo.src_large ? `${size}px` : undefined}
          width={size}
          height={size}
          alt=""
          loading={loading}
          fetchpriority={loading === 'eager' ? 'low' : undefined}
          decoding="async"
          onLoad={() => setLoadedSrc(photo.src)}
          onError={() => setFailedSrc(photo.src)}
        />
      )}
    </span>
  );
}
